import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CircularProgress, Container, Divider, Stack, Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';

const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';
const STRIPE_PK = 'pk_live_51Q5pO5RqULsyaFnGO4c7JjHvqYv9Eu4FovYOlgCfuWZyrpYgn33p1cZavi28z41Nxpo3PiKLbscqL7c2ezQUFux400HaYeh7o6';

const CARD_EL_STYLE = {
  base: { fontSize: '16px', color: '#333', '::placeholder': { color: '#999' } },
  invalid: { color: '#d40100' },
};

const formatPrice = (c) => `$${((Number(c) || 0) / 100).toFixed(2)}`;
const formatTime12 = (time) => {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};
const formatDateLong = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('es-PR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

// Página pública de pago (pedido 2026-10-07): el staff inscribió al cliente
// (curso privado o curso regular) y le mandó por SMS/email un link a
// /pay?t=<token>. Aquí paga con tarjeta y la reservación pasa a confirmada.
// No hace falta login: el token es la llave.
export default function PayReservationPage() {
  const [params] = useSearchParams();
  const token = params.get('t') || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reservation, setReservation] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paid, setPaid] = useState(false);
  const stripeRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    (async () => {
      if (!token) { setError('Este link de pago no es válido.'); setLoading(false); return; }
      try {
        const r = await axios.post(`${FUNCTIONS_BASE}/getPaymentLinkReservation`, { token });
        setReservation(r.data.reservation);
        if (r.data.reservation.status === 'confirmed') setPaid(true);
      } catch (e) {
        setError((e.response && e.response.data && e.response.data.error) || 'No se pudo cargar la reservación.');
      }
      setLoading(false);
    })();
  }, [token]);

  // Campos de tarjeta separados (número / vence / CVC), igual que el resto del web
  const pendingToPay = reservation && reservation.status === 'pending_payment' && !paid;
  useEffect(() => {
    if (!pendingToPay) return undefined;
    let destroyed = false;
    (async () => {
      try {
        const stripeObj = stripeRef.current || await loadStripe(STRIPE_PK);
        if (destroyed) return;
        stripeRef.current = stripeObj;
        const elements = stripeObj.elements();
        const number = elements.create('cardNumber', { style: CARD_EL_STYLE, placeholder: '1234 1234 1234 1234' });
        const expiry = elements.create('cardExpiry', { style: CARD_EL_STYLE });
        const cvc = elements.create('cardCvc', { style: CARD_EL_STYLE });
        const parts = { number: false, expiry: false, cvc: false };
        const watch = (k, el) => el.on('change', (ev) => {
          parts[k] = ev.complete === true;
          setCardComplete(parts.number && parts.expiry && parts.cvc);
          setCardError(ev.error ? ev.error.message : '');
        });
        watch('number', number); watch('expiry', expiry); watch('cvc', cvc);
        number.mount('#pay-card-number'); expiry.mount('#pay-card-expiry'); cvc.mount('#pay-card-cvc');
        cardRef.current = { number, expiry, cvc };
      } catch (e) { console.error(e); }
    })();
    return () => {
      destroyed = true;
      try { if (cardRef.current) { cardRef.current.number.destroy(); cardRef.current.expiry.destroy(); cardRef.current.cvc.destroy(); } } catch (e) { /* noop */ }
      cardRef.current = null;
    };
  }, [pendingToPay]);

  const handlePay = async () => {
    if (!cardComplete) { setCardError('Completa número, vencimiento (MM/AA) y CVC de la tarjeta.'); return; }
    setProcessing(true); setCardError('');
    try {
      const r = await axios.post(`${FUNCTIONS_BASE}/createPaymentLinkIntent`, { token });
      const { clientSecret, paymentIntentId } = r.data;
      const stripeObj = stripeRef.current || await loadStripe(STRIPE_PK);
      const res = await stripeObj.confirmCardPayment(clientSecret, { payment_method: { card: cardRef.current.number } });
      if (res.error) throw new Error(res.error.message);
      const c = await axios.post(`${FUNCTIONS_BASE}/confirmPaymentLinkPayment`, { token, paymentIntentId });
      if (c.data.status !== 'confirmed') throw new Error('El pago no se completó. Intenta de nuevo.');
      setReservation(c.data.reservation || { ...reservation, status: 'confirmed' });
      setPaid(true);
    } catch (e) {
      setCardError((e.response && e.response.data && e.response.data.error) || e.message || 'No se pudo procesar el pago');
    }
    setProcessing(false);
  };

  const isCourse = reservation && (reservation.categoryType === 'course' || reservation.categoryType === 'class');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8F9FA' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: '#fff', py: 3, mb: 3 }}>
        <Container maxWidth="sm">
          <Typography variant="h5" sx={{ fontWeight: 800 }}>B&B Target Center</Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>Pago de {isCourse ? 'inscripción' : 'reservación'}</Typography>
        </Container>
      </Box>
      <Container maxWidth="sm" sx={{ pb: 6 }}>
        {loading && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && reservation && (
          <Card sx={{ p: 3, borderRadius: 3 }}>
            {paid ? (
              <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: '#22C55E' }} />
                <Typography variant="h5" fontWeight={800}>¡Pago recibido!</Typography>
                <Typography variant="body1">
                  Tu {isCourse ? 'inscripción a' : 'reservación de'} <strong>{reservation.categoryName}</strong> quedó confirmada.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateLong(reservation.date)} · {formatTime12(reservation.startTime)} – {formatTime12(reservation.endTime)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Código: <strong style={{ fontFamily: 'monospace', letterSpacing: 2 }}>{reservation.confirmationCode}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">Te enviamos la confirmación por email.</Typography>
                <Button href="/my-reservations" variant="outlined" sx={{ mt: 1 }}>Ver mis reservaciones</Button>
              </Stack>
            ) : reservation.status !== 'pending_payment' ? (
              <Alert severity="warning">
                Esta {isCourse ? 'inscripción' : 'reservación'} ya no está pendiente de pago
                {reservation.status === 'cancelled' ? ' (fue cancelada)' : reservation.status === 'expired' ? ' (expiró)' : ''}.
                Si crees que es un error, comunícate con B&B Target Center.
              </Alert>
            ) : (
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={800}>Hola {reservation.userName.split(' ')[0] || ''}</Typography>
                <Typography variant="body2" color="text.secondary">
                  El equipo de B&B te inscribió. Completa el pago para confirmar tu {isCourse ? 'cupo en el curso' : 'reservación'}.
                </Typography>
                <Box sx={{ p: 2, bgcolor: '#F4F6F8', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>{reservation.categoryName}</Typography>
                  <Typography variant="body2">{formatDateLong(reservation.date)}</Typography>
                  <Typography variant="body2">{formatTime12(reservation.startTime)} – {formatTime12(reservation.endTime)}{reservation.laneName ? ` · ${reservation.laneName}` : ''}</Typography>
                  {reservation.courseLanguage && (
                    <Typography variant="caption" color="text.secondary">
                      {reservation.courseLanguage === 'en' ? 'Inglés' : 'Español'}{reservation.licenseType ? ` · ${reservation.licenseType === 'renewal' ? 'Renovación' : 'Licencia nueva'}` : ''}
                    </Typography>
                  )}
                </Box>
                <Stack spacing={0.5}>
                  {reservation.taxCents > 0 && (
                    <>
                      <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Subtotal{reservation.isMember ? ' (precio de miembro)' : ''}</Typography><Typography variant="body2">{formatPrice(reservation.subtotalCents)}</Typography></Stack>
                      <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">IVU 11.5%</Typography><Typography variant="body2">{formatPrice(reservation.taxCents)}</Typography></Stack>
                    </>
                  )}
                  {reservation.depositOnly && (
                    <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Precio total</Typography><Typography variant="body2">{formatPrice(reservation.totalCents)}</Typography></Stack>
                  )}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={700}>{reservation.depositOnly ? 'Depósito a pagar ahora' : 'Total a pagar'}</Typography>
                    <Typography variant="h5" color="#d40100" fontWeight={800}>{formatPrice(reservation.amountCents)}</Typography>
                  </Stack>
                  {reservation.depositOnly && (
                    <Typography variant="caption" color="text.secondary">El restante de {formatPrice(reservation.totalCents - reservation.amountCents)} se paga en persona el día de la cita.</Typography>
                  )}
                </Stack>
                <Divider />
                <Typography variant="caption" color="text.secondary" fontWeight={700}>TARJETA</Typography>
                <Box id="pay-card-number" sx={{ p: 1.75, border: '1px solid #ddd', borderRadius: 1, minHeight: 44, bgcolor: '#fff' }} />
                <Stack direction="row" spacing={1}>
                  <Box id="pay-card-expiry" sx={{ p: 1.75, border: '1px solid #ddd', borderRadius: 1, minHeight: 44, flex: 1, bgcolor: '#fff' }} />
                  <Box id="pay-card-cvc" sx={{ p: 1.75, border: '1px solid #ddd', borderRadius: 1, minHeight: 44, flex: 1, bgcolor: '#fff' }} />
                </Stack>
                {cardError && <Alert severity="error">{cardError}</Alert>}
                <Button
                  variant="contained"
                  size="large"
                  startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <LockIcon />}
                  onClick={handlePay}
                  disabled={processing}
                  sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#a30000' }, py: 1.5 }}
                >
                  {processing ? 'Procesando…' : `Pagar ${formatPrice(reservation.amountCents)}`}
                </Button>
                <Typography variant="caption" color="text.secondary" align="center">Pago seguro procesado por Stripe.</Typography>
              </Stack>
            )}
          </Card>
        )}
      </Container>
    </Box>
  );
}
